import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage, useRotatingTagline } from '../i18n';
import { Tooltip } from './Tooltip';
import { NotificationBell } from './NotificationBell';
import styles from './Layout.module.css';

interface LayoutProps {
  children: React.ReactNode;
}

export function Layout({ children }: LayoutProps) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const { t, language, toggleLanguage } = useLanguage();
  const { tagline, isFading } = useRotatingTagline();

  // Calculate days left until membership expires
  const getDaysLeft = (): number | null => {
    if (!user?.membershipValidTill) return null;
    const expiryDate = new Date(user.membershipValidTill);
    const now = new Date();
    const diffTime = expiryDate.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 0 ? diffDays : 0;
  };

  const daysLeft = getDaysLeft();
  const isExpired = user?.isExpired || daysLeft === 0;
  const isExpiringSoon = !isExpired && daysLeft !== null && daysLeft <= 7;

  return (
    <div className={styles.layout}>
      {isExpired && (
        <div className={styles.expiredBanner}>
          <span>
            {language === 'tr'
              ? '⚠️ Üyeliğinizin süresi doldu. Emirleriniz devre dışı bırakıldı.'
              : '⚠️ Your membership has expired. Your orders have been deactivated.'}
          </span>
          <Link to="/payment-plans" className={styles.renewLink}>
            {language === 'tr' ? 'Şimdi Yenile' : 'Renew Now'}
          </Link>
        </div>
      )}
      {isExpiringSoon && (
        <div className={styles.warningBanner}>
          <span>
            {language === 'tr'
              ? `⏰ Üyeliğinizin süresinin dolmasına ${daysLeft} gün kaldı.`
              : `⏰ Your membership expires in ${daysLeft} day${daysLeft === 1 ? '' : 's'}.`}
          </span>
          <Link to="/payment-plans" className={styles.renewLinkWarning}>
            {language === 'tr' ? 'Şimdi Yenile' : 'Renew Now'}
          </Link>
        </div>
      )}
      <header className={styles.header}>
        <div className={styles.headerContent}>
          <Link to="/" className={styles.logo}>
            <img src="/polaris.svg" alt="Polaris" width="32" height="32" />
            <div className={styles.logoText}>
              <span>Polaris</span>
              <span className={styles.tagline} style={{ opacity: isFading ? 0 : 0.7 }}>{tagline}</span>
            </div>
          </Link>

          <nav className={styles.nav}>
            <Link
              to="/"
              className={`${styles.navLink} ${location.pathname === '/' ? styles.active : ''}`}
            >
              {t.nav.dashboard}
            </Link>
          </nav>

          <div className={styles.userMenu}>
            <NotificationBell variant="header" />
            <Link to="/settings" className={styles.settingsBtn} title={language === 'tr' ? 'Ayarlar' : 'Settings'}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="3"></circle>
                <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path>
              </svg>
            </Link>
            <Link to="/help" className={styles.helpBtn} title={t.help.title}>
              ?
            </Link>
            <button onClick={toggleLanguage} className={styles.langBtn}>
              {language === 'tr' ? 'EN' : 'TR'}
            </button>
            <span className={styles.userName}>{user?.name}</span>
            <Tooltip text={t.paymentPlans.tooltipText} linkTo="/payment-plans" position="bottom">
              {(() => {
                const tier = user?.membershipTier ?? 0;
                // Names are pre-uppercased here so we don't rely on CSS
                // `text-transform: uppercase`, which under the Turkish locale
                // (html lang="tr") would render "Silver" as "SİLVER" because
                // dotted-i casing rules. Hardcoded uppercase keeps it Latin.
                const names: Record<number, string> = {
                  0: 'DEMO', 1: 'BRONZE', 2: 'BRONZE+', 3: 'SILVER', 4: 'SILVER+',
                  5: 'GOLD', 6: 'GOLD+', 7: 'PLATINUM', 8: 'PLATINUM+',
                  9: 'TYCOON', 10: 'TYCOON+',
                };
                const name = names[tier] ?? `TIER ${tier}`;
                // tierClass maps tier id to one of: demo / bronze / silver / gold / platinum / tycoon
                const tierClass =
                  tier === 0 ? 'tierDemo' :
                  tier <= 2 ? 'tierBronze' :
                  tier <= 4 ? 'tierSilver' :
                  tier <= 6 ? 'tierGold' :
                  tier <= 8 ? 'tierPlatinum' :
                              'tierTycoon';
                return (
                  <span className={`${styles.membershipBadge} ${styles[tierClass]}`}>
                    {name}
                  </span>
                );
              })()}
            </Tooltip>
            {daysLeft !== null && (
              <span className={styles.daysLeft}>
                {t.paymentPlans.daysLeft.replace('{days}', String(daysLeft))}
              </span>
            )}
            <button onClick={logout} className={styles.logoutBtn}>
              {t.nav.logout}
            </button>
          </div>
        </div>
      </header>

      <main className={styles.main}>
        <div className="container">
          {children}
        </div>
      </main>

      <footer className={styles.footer}>
        <div className={styles.footerContentSimple}>
          <span className={styles.footerText}>{t.footer.aPolysanProduct}</span>
          <span className={styles.productCode}>PLSN-003-0246X</span>
          <span className={styles.footerText}>&copy; {t.footer.copyright} {new Date().getFullYear()}</span>
        </div>
      </footer>
    </div>
  );
}
